/**
 * The modal and the right-hand drawer, at the sizes and weights the design
 * brief specifies.
 *
 * `components/Modal/ModalWrapper` is not reused here: it is fixed at 624px
 * with a hard-coded white surface, and these need 780px (the wizard), 640px
 * (the member profile) and the dashboard's own tokens so dark mode keeps
 * working. The overlay opacities and shadows below are the brief's values.
 */
import React, {useEffect} from 'react';
import {XMarkIcon} from '@heroicons/react/24/outline';

/** Esc closes, and the page behind stops scrolling while either is open. */
function useDismiss(onClose: () => void) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const {overflow} = document.body.style;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
    };
  }, [onClose]);
}

interface ModalProps {
  title: string;
  sub?: string;
  width?: number;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  /** 0 to 100. Drawn under the header, for the wizard's steps. */
  progress?: number | null;
}

export const Modal = ({
  title,
  sub,
  width = 640,
  onClose,
  children,
  footer,
  progress = null,
}: ModalProps) => {
  useDismiss(onClose);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto py-10 px-4"
      style={{background: 'rgba(15,15,26,.42)'}}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="w-full bg-surface rounded-2xl overflow-hidden"
        style={{maxWidth: width, boxShadow: '0 24px 64px rgba(15,15,26,.24)'}}
      >
        <div className="px-6 pt-5 pb-4 border-b border-borderc">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-[17px] font-semibold text-text1 leading-tight">{title}</h2>
              {sub && <p className="text-[12.5px] text-text3 mt-1">{sub}</p>}
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="w-8 h-8 rounded-lg flex items-center justify-center text-text3 hover:bg-surface2 hover:text-text1 transition-colors"
            >
              <XMarkIcon className="w-[18px] h-[18px]" />
            </button>
          </div>
          {progress != null && (
            <div className="mt-4 h-1 rounded-full bg-surface3 overflow-hidden">
              <div
                className="h-full bg-brand rounded-full transition-all duration-300"
                style={{width: `${Math.max(0, Math.min(100, progress))}%`}}
              />
            </div>
          )}
        </div>

        <div className="px-6 py-5">{children}</div>

        {footer && (
          <div className="px-6 py-4 border-t border-borderc bg-surface2 flex items-center justify-between gap-3">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

interface DrawerProps {
  title: string;
  sub?: string;
  width?: number;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  tabs?: {key: string; label: string}[];
  activeTab?: string;
  onTab?: (key: string) => void;
}

export const Drawer = ({
  title,
  sub,
  width = 640,
  onClose,
  children,
  footer,
  tabs,
  activeTab,
  onTab,
}: DrawerProps) => {
  useDismiss(onClose);

  return (
    <div
      className="fixed inset-0 z-[60] flex justify-end"
      style={{background: 'rgba(15,15,26,.36)'}}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="h-full w-full bg-surface flex flex-col"
        style={{maxWidth: width, boxShadow: '-12px 0 40px rgba(15,15,26,.16)'}}
      >
        <div className="px-6 pt-5 pb-3 border-b border-borderc flex-shrink-0">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h2 className="text-[18px] font-semibold text-text1 leading-tight truncate">{title}</h2>
              {sub && <p className="text-[12.5px] text-text3 mt-1 truncate">{sub}</p>}
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="w-8 h-8 rounded-lg flex items-center justify-center text-text3 hover:bg-surface2 hover:text-text1 transition-colors flex-shrink-0"
            >
              <XMarkIcon className="w-[18px] h-[18px]" />
            </button>
          </div>

          {tabs && (
            <div className="flex gap-1 mt-4 -mb-3 overflow-x-auto no-scrollbar">
              {tabs.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => onTab?.(t.key)}
                  className={`px-3 py-2.5 text-[13px] whitespace-nowrap border-b-2 transition-colors ${
                    activeTab === t.key
                      ? 'border-brand text-text1 font-semibold'
                      : 'border-transparent text-text3 hover:text-text2'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>

        {footer && (
          <div className="px-6 py-4 border-t border-borderc bg-surface2 flex items-center gap-3 flex-shrink-0">
            {footer}
          </div>
        )}
      </aside>
    </div>
  );
};

/** The primary and secondary buttons, at the brief's 38–40px height. */
export const PrimaryButton = ({
  children,
  onClick,
  disabled,
  type = 'button',
  tone = 'brand',
}: {
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  type?: 'button' | 'submit';
  tone?: 'brand' | 'danger';
}) => (
  <button
    type={type}
    onClick={onClick}
    disabled={disabled}
    className={`h-10 px-4 rounded-[9px] text-[13.5px] font-semibold text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
      tone === 'danger' ? 'bg-neg hover:brightness-95' : 'bg-brand hover:brightness-110'
    }`}
  >
    {children}
  </button>
);

export const GhostButton = ({
  children,
  onClick,
  disabled,
  title,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  /** Shown on hover — the place to say why a disabled control is disabled. */
  title?: string;
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    className="h-10 px-4 rounded-[9px] text-[13.5px] font-medium text-text2 bg-surface border border-borderc hover:bg-surface2 transition-colors disabled:opacity-50"
  >
    {children}
  </button>
);
